import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import { Box, CircularProgress } from '@mui/material';

// Lazy load all pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const ContactListPage = lazy(() => import('./pages/ContactListPage'));
const UnsubscribedPage = lazy(() => import('./pages/UnsubscribedPage'));
const ContactRecordPage = lazy(() => import('./pages/ContactRecordPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const DemoCalendarPage = lazy(() => import('./pages/DemoCalendarPage'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const PartnerUserImportPage = lazy(() => import('./pages/PartnerUserImportPage'));
const ImportedContactsPage = lazy(() => import('./pages/ImportedContactsPage'));
const BookingFormPage = lazy(() => import('./pages/BookingFormPage'));
const SpyCloudLeadPage = lazy(() => import('./pages/SpyCloudLeadPage'));
const PlatformStrategyFormPage = lazy(() => import('./pages/PlatformStrategyFormPage'));
const PreRecordedDemoFormPage = lazy(() => import('./pages/PreRecordedDemoFormPage'));
const ContactUsFormPage = lazy(() => import('./pages/ContactUsFormPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MarketingPage = lazy(() => import('./pages/MarketingPage'));
const SocialPage = lazy(() => import('./pages/SocialPage'));
const AutomationPage = lazy(() => import('./pages/AutomationPage'));
const GdprPage = lazy(() => import('./pages/GdprPage'));
const EmailLogPage = lazy(() => import('./pages/EmailLogPage'));
const PlatformGuidePage = lazy(() => import('./pages/PlatformGuidePage'));
const RequestsPage = lazy(() => import('./pages/RequestsPage'));
const PublicDashboardPage = lazy(() => import('./pages/PublicDashboardPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
    <CircularProgress sx={{ color: '#61A0AF' }} size={28} />
  </Box>
);

function ProtectedRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#61A0AF' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/book-demo" element={<BookingFormPage />} />
              <Route path="/spycloud" element={<SpyCloudLeadPage />} />
              <Route path="/platform-strategy" element={<PlatformStrategyFormPage />} />
              <Route path="/pre-recorded-demo" element={<PreRecordedDemoFormPage />} />
              <Route path="/contact-us" element={<ContactUsFormPage />} />
              <Route path="/demo" element={<DemoPage />} />
              {/* The sales wallboard. Outside the authenticated block on
                  purpose — a screen on a wall has nobody to log it in. The API
                  behind it is gated on the key in the URL. */}
              <Route path="/dashboard" element={<PublicDashboardPage />} />
              <Route element={<ProtectedRoutes />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/company" element={<CompanyPage />} />
                <Route path="/company/:id" element={<CompanyPage />} />
                <Route path="/contacts" element={<ContactListPage />} />
                <Route path="/contacts/:id" element={<ContactRecordPage />} />
                <Route path="/unsubscribed" element={<UnsubscribedPage />} />
                <Route path="/pipeline" element={<PipelinePage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/demos" element={<DemoCalendarPage />} />
                <Route path="/metrics" element={<MetricsPage />} />
                <Route path="/team" element={<UserManagementPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/notifications/:id" element={<NotificationsPage />} />
                <Route path="/import" element={<ImportPage />} />
                <Route path="/import/partner-users" element={<PartnerUserImportPage />} />
                <Route path="/imports/:batchId" element={<ImportedContactsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/marketing" element={<MarketingPage />} />
                <Route path="/social" element={<SocialPage />} />
                <Route path="/automation" element={<AutomationPage />} />
                <Route path="/gdpr" element={<GdprPage />} />
                <Route path="/email-log" element={<EmailLogPage />} />
                <Route path="/platform-guide" element={<PlatformGuidePage />} />
                <Route path="/requests" element={<RequestsPage />} />
              </Route>
              {/* Outside the protected block so it works signed out too. Without
                  a catch-all, any address the router did not know rendered an
                  entirely blank page — indistinguishable from the app being
                  broken, which is exactly how the TV dashboard looked when it
                  was opened before the deploy that added its route. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
